// @refresh reset
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { AuthContext, AuthContextType, ActiveDevice } from './AuthContextDef';
import { supabase } from '@/integrations/supabase/client';
import { getDeviceInfo } from '@/lib/deviceFingerprint';
import { useToast } from '@/hooks/use-toast';
import { createDeduplicatedRequest, createNetworkAwareOperation } from '@/lib/networkUtils';

// Environment check for development mode
const isDev = import.meta.env.DEV;

// Security: Log suspicious authentication events (only critical events)
const logSecurityEvent = (event: string, details: Record<string, unknown>) => {
  // In production, send to logging service; in dev, log to console
  if (isDev) {
    console.warn(`[Security Event] ${event}:`, details);
  }
  // TODO: In production, send to a secure logging service
};





export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<'admin' | 'user' | null>(null);
  const [userProfile, setUserProfile] = useState<{ first_name?: string; last_name?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [authBlocked, setAuthBlocked] = useState(false); // Prevent auto-login during verification
  const authBlockedRef = useRef(false);
  const { toast } = useToast();

  const fetchUserRole = async (userId: string) => {
    return createDeduplicatedRequest(
      `user-role-${userId}`,
      () => createNetworkAwareOperation(async () => {
        // Use RPC to get role with SECURITY DEFINER to avoid RLS issues
        const { data, error } = await supabase.rpc('get_current_user_role');

        if (error) {
          throw error;
        }

        const role = (data as string | null) || 'user';
        setUserRole(role as 'admin' | 'user');
        return role;
      }, 'fetch user role'),
      60000 // Cache for 1 minute
    ).catch(error => {
      if (isDev) console.error('Error fetching user role:', error);
      setUserRole('user'); // Always set a fallback role
      return 'user';
    });
  };

  const fetchUserProfile = async (userId: string) => {
    return createDeduplicatedRequest(
      `user-profile-${userId}`,
      () => createNetworkAwareOperation(async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name')
          .eq('user_id', userId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        setUserProfile(data);
        return data;
      }, 'fetch user profile'),
      60000 // Cache for 1 minute
    ).catch(error => {
      if (isDev) console.error('Error fetching user profile:', error);
      setUserProfile(null);
      return null;
    });
  };

  const checkUserLockStatus = async (userId: string) => {
    return createDeduplicatedRequest(
      `user-lock-${userId}`,
      () => createNetworkAwareOperation(async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('is_locked')
          .eq('user_id', userId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        return data?.is_locked || false;
      }, 'check user lock status'),
      30000 // Cache for 30 seconds
    ).catch(error => {
      if (isDev) console.error('Error checking user lock status:', error);
      logSecurityEvent('lock_status_check_failed', { userId, error: String(error) });
      toast({
        title: 'Connection Error',
        description: 'Unable to verify your account status. Please check your connection and try again.',
        variant: 'destructive'
      });
      return true; // Fail secure: assume locked on error to prevent unauthorized access
    });
  };

  const loadUserData = useCallback(async (userId: string) => {
    try {
      const [role, profile, isLocked] = await Promise.all([
        fetchUserRole(userId),
        fetchUserProfile(userId),
        checkUserLockStatus(userId)
      ]);

      if (isLocked) {
        if (isDev) console.log('AuthContext: User is locked, signing out');
        await supabase.auth.signOut();
        setAuthBlocked(true);
        toast({
          title: 'Account Locked',
          description: 'Your account has been locked. Please contact support for assistance.',
          variant: 'destructive'
        });
        return;
      }

      // Check subscription status for non-admin users
      if (role !== 'admin') {
        try {
          const { data: hasSubscription, error: subscriptionError } = await supabase.rpc('user_has_active_subscription', {
            user_uuid: userId
          });

          if (subscriptionError) {
            // Security: Fail secure - block access on subscription check errors
            logSecurityEvent('subscription_check_failed', { userId, error: String(subscriptionError) });
            if (isDev) console.error('Error checking subscription status:', subscriptionError);
            await supabase.auth.signOut();
            setAuthBlocked(true);
            toast({
              title: 'Authentication Error',
              description: 'Unable to verify subscription status. Please try again.',
              variant: 'destructive'
            });
            return;
          }

          if (!hasSubscription) {
            if (isDev) console.log('AuthContext: User does not have active subscription, blocking access');
            await supabase.auth.signOut();
            setAuthBlocked(true);
            toast({
              title: 'Subscription Required',
              description: 'Your subscription is inactive. Please renew your subscription to continue.',
              variant: 'destructive'
            });
            return;
          }
        } catch (error) {
          // Security: Fail secure - block access on any error
          logSecurityEvent('subscription_check_exception', { userId, error: String(error) });
          if (isDev) console.error('Error checking subscription status:', error);
          await supabase.auth.signOut();
          setAuthBlocked(true);
          toast({
            title: 'Authentication Error',
            description: 'Unable to verify subscription status. Please try again.',
            variant: 'destructive'
          });
          return;
        }
      }

      setUserRole(role as 'admin' | 'user');
      setUserProfile(profile);
    } catch (error) {
      if (isDev) console.error('Error fetching user data:', error);
    }
  }, []);

  const handleAuthStateChange = useCallback(async (event: string, session: Session | null) => {
    if (isDev) console.log('AuthContext: Auth state change:', event, 'Session:', session?.user?.id);

    // Use ref to always read the latest authBlocked value (avoids stale closure)
    if (authBlockedRef.current) {
      if (isDev) console.log('AuthContext: Auth blocked, not setting user state');
      return;
    }

    setSession(session);
    setUser(session?.user ?? null);

    if (session?.user) {
      // Don't block loading on user data fetch — let the UI render immediately
      loadUserData(session.user.id);
    } else {
      setUserRole(null);
      setUserProfile(null);
    }

    setLoading(false);
  }, [loadUserData]);

  // Use ref to track if we've already set up the listener to prevent memory leaks
  const subscriptionRef = useRef<{ unsubscribe: () => void } | null>(null);
  const isInitializedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple subscriptions
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    if (isDev) console.log('AuthContext: Setting up auth state listener');

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthStateChange);
    subscriptionRef.current = subscription;

    // Check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (isDev) console.log('AuthContext: Initial session check:', session?.user?.id);

      if (!authBlockedRef.current) {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          // Don't block loading on user data fetch — let the UI render immediately
          loadUserData(session.user.id);
        }
      }

      setLoading(false);
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, []); // Empty dependency array - only run once on mount

  // Keep authBlockedRef in sync with state
  useEffect(() => {
    authBlockedRef.current = authBlocked;
    if (authBlocked) {
      setUser(null);
      setSession(null);
    }
  }, [authBlocked]);

  const signIn = async (email: string, password: string, forceLogin = false) => {
    try {
      // Block auth state changes during verification flow
      setAuthBlocked(true);

      // Get device information (async for SHA-256 hashing)
      const deviceInfo = await getDeviceInfo();

      if (isDev) console.log('AuthContext: Starting sign in process...');
      
      // First, attempt to sign in with Supabase
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        if (isDev) console.log('AuthContext: Auth error:', authError);
        setAuthBlocked(false);
        return { error: authError };
      }

      if (!authData.user || !authData.session) {
        if (isDev) console.log('AuthContext: No user or session in auth data');
        setAuthBlocked(false);
        return { error: new Error('Authentication failed') };
      }

      if (isDev) console.log('AuthContext: Successfully authenticated user:', authData.user.id);

      // Security: Don't expose user IP to third-party services
      // IP logging should be handled server-side if needed for security
      // Use null to let the database handle it or skip IP-based features
      const userIP: string | null = null;

      // Call our device session management function
      const { data: sessionResult, error: sessionError } = await supabase.rpc(
        'manage_device_session',
        {
          user_uuid: authData.user.id,
          device_name_param: deviceInfo.deviceName,
          device_type_param: deviceInfo.deviceType,
          browser_info_param: deviceInfo.browserInfo,
          ip_address_param: userIP,
          device_fingerprint_param: deviceInfo.deviceFingerprint,
          location_info_param: deviceInfo.locationInfo,
          session_token_param: authData.session.access_token,
          force_login: forceLogin
        }
      );

      if (sessionError) {
        if (isDev) console.error('AuthContext: Session management error:', sessionError);
        // Sign out the user since we couldn't manage the session properly
        await supabase.auth.signOut();
        setAuthBlocked(false);
        return { error: sessionError };
      }

      const result = sessionResult as Record<string, unknown>;
      if (isDev) console.log('AuthContext: Device session result:', result);

      if (!result?.success) {
        if (isDev) console.log('AuthContext: Session not successful, checking reason...');

        // Check if verification is required for new device
        if (result?.reason === 'verification_required') {
          if (isDev) console.log('AuthContext: Verification required - signing out to prevent auto-login');
          if (isDev) console.log('AuthContext: Full result object:', JSON.stringify(result, null, 2));

          // Store session tokens BEFORE signing out so they can be restored after verification
          const savedSession = {
            access_token: authData.session.access_token,
            refresh_token: authData.session.refresh_token
          };

          // CRITICAL: Sign out immediately to prevent the session from persisting
          // This prevents the onAuthStateChange from setting user state
          await supabase.auth.signOut();

          // Return verification data with the saved session tokens
          const returnValue = {
            error: new Error(result.message as string || 'Device verification required'),
            requiresVerification: true,
            verificationData: {
              email: result.user_email as string,
              deviceFingerprint: result.device_fingerprint as string
            },
            tempSession: savedSession
          };

          if (isDev) console.log('AuthContext: Returning verification response');
          return returnValue;
        }
        
        // There's an active session on another device (for admin users or force login scenario)
        if (result?.reason === 'active_session_exists') {
          // Sign out the current session since we can't proceed
          await supabase.auth.signOut();
          setAuthBlocked(false);
          
          // Get active devices for display
          const { data: activeSessionsData } = await supabase.rpc('check_active_sessions', {
            user_uuid: authData.user.id
          });

          const deviceData = activeSessionsData as unknown as { devices: ActiveDevice[] };

          return {
            error: new Error(result.message as string || 'Active session exists'),
            requiresForceLogin: true,
            activeDevices: deviceData?.devices || [],
            debugInfo: {
              userRole: result?.user_role,
              isAdmin: result?.is_admin,
              isSameDevice: result?.is_same_device
            }
          };
        }
        
        // Sign out and return the error
        await supabase.auth.signOut();
        setAuthBlocked(false);
        return { error: new Error(result?.message as string || 'Session management failed') };
      }

      // If we reach here, device session was successful. Now check lock status.
      if (isDev) console.log('AuthContext: Device session successful, checking lock status...');

      // Check if user account is locked
      const isLocked = await checkUserLockStatus(authData.user.id);
      if (isLocked) {
        if (isDev) console.log('AuthContext: User is locked, denying login');
        await supabase.auth.signOut();
        setAuthBlocked(false);
        return {
          error: new Error('Your account has been locked. Please contact support for assistance.'),
        };
      }

      // Check if user is admin
      const isAdmin = result?.is_admin || result?.user_role === 'admin';

      // For non-admin users, check subscription status before allowing access
      if (!isAdmin) {
        try {
          if (isDev) console.log('AuthContext: Checking subscription for non-admin user...');
          const { data: hasSubscription, error: subscriptionError } = await supabase.rpc('user_has_active_subscription', {
            user_uuid: authData.user.id
          });

          // Security: Fail secure on subscription check errors
          if (subscriptionError) {
            logSecurityEvent('signin_subscription_check_failed', { userId: authData.user.id, error: String(subscriptionError) });
            if (isDev) console.error('AuthContext: Error checking subscription:', subscriptionError);
            await supabase.auth.signOut();
            setAuthBlocked(false);
            return {
              error: new Error('Unable to verify subscription status. Please try again.'),
            };
          }

          if (!hasSubscription) {
            if (isDev) console.log('AuthContext: User does not have active subscription');
            await supabase.auth.signOut();
            setAuthBlocked(false);
            return {
              error: new Error('Active subscription required. Please subscribe to access the platform.'),
              requiresSubscription: true,
              userEmail: email
            };
          }
        } catch (subscriptionError) {
          // Security: Fail secure on any exception
          logSecurityEvent('signin_subscription_check_exception', { userId: authData.user.id, error: String(subscriptionError) });
          if (isDev) console.error('AuthContext: Error checking subscription:', subscriptionError);
          await supabase.auth.signOut();
          setAuthBlocked(false);
          return {
            error: new Error('Unable to verify subscription status. Please try again.'),
          };
        }
      }
      
      setAuthBlocked(false);

      // Manually set user state since onAuthStateChange was blocked during sign-in
      setUser(authData.user);
      setSession(authData.session);
      await fetchUserRole(authData.user.id);
      await fetchUserProfile(authData.user.id);

      // If suspicious activity was detected, show a toast
      if (result?.suspicious_activity) {
        toast({
          title: "Security Alert",
          description: "Suspicious activity detected. Please verify this login is legitimate.",
          variant: "destructive"
        });
      }

      // Log successful admin bypass for debugging
      if (result?.admin_bypass) {
        if (isDev) console.log('Admin successfully bypassed device restrictions');
        toast({
          title: "Admin Access",
          description: "Logged in with admin privileges - multiple device access allowed.",
        });
      }

      return { error: null };
    } catch (error) {
      if (isDev) console.error('AuthContext: Sign in error:', error);
      setAuthBlocked(false);
      return { error: error as Error };
    }
  };

const signUp = async (email: string, password: string, userData?: Record<string, unknown>) => {
const redirectUrl = `${window.location.origin}/login?emailConfirmed=true`;
  
const { error } = await supabase.auth.signUp({
email,
password,
options: {
emailRedirectTo: redirectUrl,
data: userData
}
});
return { error };
};

const signOut = async () => {
if (isDev) console.log('AuthContext: Starting sign out process');

try {
// Unblock auth state changes
setAuthBlocked(false);

// Deactivate the current device session
if (user?.id) {
if (isDev) console.log('AuthContext: Terminating user sessions for user:', user.id);

try {
await supabase.rpc('terminate_user_sessions', {
target_user_id: user.id
});
if (isDev) console.log('AuthContext: Successfully terminated user sessions');
} catch (terminateError) {
if (isDev) console.error('AuthContext: Error terminating user sessions:', terminateError);
// Continue with logout even if session termination fails
}
}
  
// Clear local state first
setUser(null);
setSession(null);
setUserRole(null);
  
// Sign out from Supabase
const { error } = await supabase.auth.signOut();
if (error) {
if (isDev) console.error('AuthContext: Error during Supabase sign out:', error);
// Don't throw error here - we want to complete the logout process
// This might be a "Session not found" error which is acceptable
}
  
if (isDev) console.log('AuthContext: Sign out completed successfully');
} catch (error) {
if (isDev) console.error('AuthContext: Error during sign out:', error);
// Even if there's an error, ensure local state is cleared
setUser(null);
setSession(null);
setUserRole(null);
setAuthBlocked(false);
}
};

const cleanupTempSession = async () => {
if (isDev) console.log('AuthContext: Cleaning up temporary session...');
setAuthBlocked(false);
try {
await supabase.auth.signOut();
} catch (error) {
if (isDev) console.error('AuthContext: Error cleaning up temp session:', error);
}
};

// Restore user session from a given access token (for temp verification flows)
const restoreSessionFromToken = async (access_token: string) => {
try {
const { data, error } = await supabase.auth.setSession({ access_token, refresh_token: '' });
if (error) {
if (isDev) console.error('AuthContext: Error restoring session from token:', error);
return;
}
setSession(data.session);
setUser(data.session?.user ?? null);
if (data.session?.user) {
await fetchUserRole(data.session.user.id);
}
} catch (error) {
if (isDev) console.error('AuthContext: Error restoring session from token:', error);
}
};

const terminateAllSessions = async () => {
if (!user?.id) return;
  
try {
await supabase.rpc('terminate_user_sessions', {
target_user_id: user.id
});
  
toast({
title: "Sessions Terminated",
description: "All active sessions have been terminated.",
});
} catch (error) {
if (isDev) console.error('Error terminating sessions:', error);
toast({
title: "Error",
description: "Failed to terminate sessions. Please try again.",
variant: "destructive"
});
}
};

const value = {
user,
session,
userRole,
userProfile,
loading,
signIn,
signUp,
signOut,
terminateAllSessions,
cleanupTempSession,
restoreSessionFromToken
};

return (
  <AuthContext.Provider value={value}>
    {children}
  </AuthContext.Provider>
);
};


